import React, { useEffect, useMemo, useState } from "react";
import ConfirmModal from "../../common/ConfirmModal";
import {
  formatVaultTableDate,
  getVaultAssociatedDocLabel,
  getVaultPublishedDocuments,
  getVaultSavedAssociatedDocuments,
} from "./qmsVaultWorkspaceUtils";

function buildOptimisticSavedRow(publishedRow) {
  return {
    DocumentIdRef: publishedRow.DocumentId,
    DocumentTitle: publishedRow.DocumentTitle,
    DocumentURL: publishedRow.DocumentURL,
    DocumentTypeAbberiviation: publishedRow.DocumentTypeAbberiviation,
    DocumentCategoryAbberiviation: publishedRow.DocumentCategoryAbberiviation,
    DocumentCategoryUniqueNumber: publishedRow.DocumentCategoryUniqueNumber,
    VersionNumber: publishedRow.VersionNumber,
    PublishedDateTime: publishedRow.PublishedDateTime,
    IsActive: 1,
  };
}

function formatDocCode(row) {
  const type = row?.DocumentTypeAbberiviation || "";
  const category = row?.DocumentCategoryAbberiviation || "";
  const unique = row?.DocumentCategoryUniqueNumber ?? "";
  const parts = [type, category, unique].filter((value) => value !== "" && value != null);
  return parts.length ? parts.join("_") : "DOC";
}

function formatPublishedDate(value) {
  const text = String(value ?? "").trim();
  if (!text) return "—";
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return formatVaultTableDate(value);
  return parsed.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function AssociatedDocPickerRow({ row, checked, disabled, onToggle, onPreview }) {
  const id = String(row.DocumentId || "");
  const code = formatDocCode(row);
  const title = row.DocumentTitle || "Untitled document";
  const canPreview = Boolean(String(row.DocumentURL || "").trim() && onPreview);

  return (
    <div className="qms-vault-associated__picker-row-wrap">
      <label className="qms-vault-associated__picker-row">
        <input
          type="checkbox"
          className="form-check-input"
          checked={checked}
          disabled={disabled}
          onChange={() => onToggle(id)}
        />
        <span className="qms-vault-associated__picker-row-body">
          <span className="qms-vault-associated__doc-code">{code}</span>
          <span className="qms-vault-associated__doc-title">{title}</span>
          <span className="qms-vault-associated__doc-meta">
            v{formatVaultTableDate(row.VersionNumber)} · Published{" "}
            {formatPublishedDate(row.PublishedDateTime)}
          </span>
        </span>
      </label>
      {canPreview ? (
        <button
          type="button"
          className="qms-vault-associated__preview-btn"
          title={`Preview ${title}`}
          aria-label={`Preview ${title}`}
          disabled={disabled}
          onClick={() => onPreview(row)}
        >
          <i className="fas fa-eye" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function AssociatedDocCard({ row, canManage, busy, removing, onRemove, onPreview }) {
  const id = String(row.DocumentIdRef || "");
  const archived = row.IsActive === false || Number(row.IsActive) === 0;
  const code = formatDocCode(row);
  const canPreview = Boolean(String(row.DocumentURL || "").trim() && onPreview);

  const handleCopyCode = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Clipboard may be unavailable; code remains visible for manual copy.
    }
  };

  return (
    <article
      className={`qms-vault-associated__card${archived ? " is-archived" : ""}${
        removing ? " is-removing" : ""
      }`}
      title={archived ? "Archived" : undefined}
    >
      <div className="qms-vault-associated__card-main">
        <h6 className="qms-vault-associated__card-title">{row.DocumentTitle || "—"}</h6>
        <div className="qms-vault-associated__card-meta">
          <button
            type="button"
            className="qms-vault-associated__card-code"
            title={`Copy document code: ${code}`}
            aria-label={`Document code ${code}. Click to copy.`}
            onClick={handleCopyCode}
          >
            {code}
          </button>
          <span>
            <i className="fas fa-code-branch" aria-hidden="true" />
            v{formatVaultTableDate(row.VersionNumber)}
          </span>
          <span>
            <i className="fas fa-calendar-check" aria-hidden="true" />
            {formatPublishedDate(row.PublishedDateTime)}
          </span>
          {archived ? (
            <span className="qms-vault-associated__archived-badge">Archived</span>
          ) : null}
        </div>
      </div>

      <div className="qms-vault-associated__card-actions">
        {canPreview ? (
          <button
            type="button"
            className="qms-vault-associated__preview-btn"
            title="Preview document"
            aria-label={`Preview ${row.DocumentTitle || "document"}`}
            disabled={busy}
            onClick={() => onPreview(row)}
          >
            <i className="fas fa-eye" aria-hidden="true" />
          </button>
        ) : null}

        {canManage ? (
          <button
            type="button"
            className="qms-vault-associated__remove-btn"
            disabled={busy}
            title="Remove associated document"
            aria-label={`Remove ${row.DocumentTitle || "document"}`}
            onClick={() => onRemove(id)}
          >
            {removing ? (
              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            ) : (
              <i className="fas fa-unlink" aria-hidden="true" />
            )}
          </button>
        ) : null}
      </div>
    </article>
  );
}

function QmsVaultAssociatedDocsPanel({
  workspaceResponse,
  workflow,
  saving = false,
  onSave,
  onRemove,
  onPreviewDocument,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmModal, setConfirmModal] = useState({ open: false, id: null });
  const [pendingAction, setPendingAction] = useState(null);
  const [removingId, setRemovingId] = useState("");
  const [optimisticLinkedRows, setOptimisticLinkedRows] = useState([]);
  const [optimisticRemovedIds, setOptimisticRemovedIds] = useState(() => new Set());

  const publishedDocs = useMemo(
    () => getVaultPublishedDocuments(workspaceResponse),
    [workspaceResponse],
  );
  const savedDocsFromServer = useMemo(
    () => getVaultSavedAssociatedDocuments(workspaceResponse),
    [workspaceResponse],
  );

  useEffect(() => {
    setOptimisticLinkedRows([]);
    setOptimisticRemovedIds(new Set());
  }, [workspaceResponse]);

  useEffect(() => {
    if (!saving) {
      setPendingAction(null);
      setRemovingId("");
    }
  }, [saving]);

  const savedDocs = useMemo(() => {
    const base = savedDocsFromServer.filter(
      (row) => !optimisticRemovedIds.has(String(row.DocumentIdRef || "")),
    );
    const existingIds = new Set(base.map((row) => String(row.DocumentIdRef || "")));
    const optimistic = optimisticLinkedRows.filter(
      (row) => !existingIds.has(String(row.DocumentIdRef || "")),
    );
    return [...base, ...optimistic];
  }, [optimisticLinkedRows, optimisticRemovedIds, savedDocsFromServer]);
  const savedIdSet = useMemo(
    () => new Set(savedDocs.map((row) => String(row.DocumentIdRef || ""))),
    [savedDocs],
  );
  const availableDocs = useMemo(
    () =>
      publishedDocs.filter(
        (row) => !savedIdSet.has(String(row.DocumentId || "")),
      ),
    [publishedDocs, savedIdSet],
  );

  const filteredAvailableDocs = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    if (!needle) return availableDocs;
    return availableDocs.filter((row) =>
      getVaultAssociatedDocLabel(row).toLowerCase().includes(needle),
    );
  }, [availableDocs, searchQuery]);

  const selectedDocs = useMemo(
    () =>
      availableDocs.filter((row) =>
        selectedIds.includes(String(row.DocumentId || "")),
      ),
    [availableDocs, selectedIds],
  );

  const toggleSelected = (id) => {
    const key = String(id);
    setSelectedIds((prev) =>
      prev.includes(key) ? prev.filter((value) => value !== key) : [...prev, key],
    );
  };

  const handleSave = async () => {
    if (!selectedIds.length || pendingAction) return;

    const idsToLink = [...selectedIds];
    const optimisticRows = publishedDocs
      .filter((row) => idsToLink.includes(String(row.DocumentId || "")))
      .map(buildOptimisticSavedRow);

    setPendingAction("link");
    setOptimisticLinkedRows((prev) => [...prev, ...optimisticRows]);
    setSelectedIds([]);
    setSearchQuery("");

    const result = await onSave?.(idsToLink.join(","));
    if (result == null) {
      setOptimisticLinkedRows((prev) =>
        prev.filter((row) => !idsToLink.includes(String(row.DocumentIdRef || ""))),
      );
    }
  };

  const handleConfirmRemove = async () => {
    const id = String(confirmModal.id || "");
    if (!id || pendingAction) return;

    setPendingAction("remove");
    setRemovingId(id);
    setOptimisticRemovedIds((prev) => new Set([...prev, id]));
    setConfirmModal({ open: false, id: null });

    const result = await onRemove?.(id);
    if (result == null) {
      setOptimisticRemovedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const canManage = Boolean(workflow?.canManageAssociatedDocs);
  const isBusy = saving || Boolean(pendingAction);

  return (
    <div className="qms-doc-workspace__panel qms-vault-associated">
      <div
        className={`qms-vault-associated__split${
          canManage ? "" : " is-linked-only"
        }`}
      >
        {canManage ? (
          <section className="qms-vault-associated__select-pane">
            <div className="qms-vault-associated__pane-head">
              <h6>Select documents</h6>
              {selectedIds.length > 0 ? (
                <span className="qms-vault-associated__selected-count">
                  {selectedIds.length} selected
                </span>
              ) : (
                <span className="qms-vault-associated__pane-meta">
                  {availableDocs.length} available
                </span>
              )}
            </div>

            <div className="qms-vault-associated__select-body">
              <div className="qms-vault-associated__intro">
                <strong>Associated documents</strong>
                <p>
                  Search published documents, select one or more, then link them to this vault
                  record.
                </p>
              </div>

              <div className="qms-vault-associated__search-wrap">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="text"
                  className="form-control form-control-sm qms-vault-associated__search-input"
                  value={searchQuery}
                  placeholder="Search by code, title, or category..."
                  disabled={isBusy}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
                {searchQuery ? (
                  <button
                    type="button"
                    className="qms-vault-associated__search-clear"
                    aria-label="Clear search"
                    onClick={() => setSearchQuery("")}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                ) : null}
              </div>

              {selectedDocs.length > 0 ? (
                <div className="qms-vault-associated__chips">
                  {selectedDocs.map((row) => {
                    const id = String(row.DocumentId || "");
                    return (
                      <button
                        key={id}
                        type="button"
                        className="qms-vault-associated__chip"
                        disabled={isBusy}
                        onClick={() => toggleSelected(id)}
                      >
                        <span>{formatDocCode(row)}</span>
                        <i className="fas fa-times" aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <div className="qms-vault-associated__picker">
                {availableDocs.length === 0 ? (
                  <div className="qms-vault-associated__picker-empty">
                    <i className="fas fa-check-circle" aria-hidden="true" />
                    <span>All published documents are already linked.</span>
                  </div>
                ) : filteredAvailableDocs.length === 0 ? (
                  <div className="qms-vault-associated__picker-empty">
                    <i className="fas fa-search" aria-hidden="true" />
                    <span>No documents match your search.</span>
                  </div>
                ) : (
                  filteredAvailableDocs.map((row) => {
                    const id = String(row.DocumentId || "");
                    return (
                      <AssociatedDocPickerRow
                        key={id}
                        row={row}
                        checked={selectedIds.includes(id)}
                        disabled={isBusy}
                        onToggle={toggleSelected}
                        onPreview={onPreviewDocument}
                      />
                    );
                  })
                )}
              </div>

              <div className="qms-vault-associated__add-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-primary qms-vault-associated__link-btn"
                  disabled={isBusy || !selectedIds.length}
                  onClick={handleSave}
                >
                  {pendingAction === "link" ? (
                    <>
                      <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                      Linking…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-link" aria-hidden="true" />
                      Link {selectedIds.length > 0 ? `${selectedIds.length} ` : ""}document
                      {selectedIds.length === 1 ? "" : "s"}
                    </>
                  )}
                </button>
                {selectedIds.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    disabled={isBusy}
                    onClick={() => setSelectedIds([])}
                  >
                    Clear selection
                  </button>
                ) : null}
              </div>
            </div>
          </section>
        ) : null}

        <section className="qms-vault-associated__linked-pane">
          <div className="qms-vault-associated__pane-head">
            <h6>Linked documents</h6>
            <span className="qms-vault-associated__pane-meta">{savedDocs.length} total</span>
          </div>

          <div className="qms-vault-associated__linked-body">
            {savedDocs.length === 0 ? (
              <div className="qms-vault-associated__empty">
                <div className="qms-vault-associated__empty-icon" aria-hidden="true">
                  <i className="fas fa-file-circle-plus" />
                </div>
                <strong>No associated documents yet</strong>
                <p>
                  {canManage
                    ? "Select documents on the left to link them here."
                    : "No published documents have been linked to this record."}
                </p>
              </div>
            ) : (
              <div className="qms-vault-associated__list">
                {savedDocs.map((row) => (
                  <AssociatedDocCard
                    key={String(row.DocumentIdRef || "")}
                    row={row}
                    canManage={canManage}
                    busy={isBusy}
                    removing={removingId === String(row.DocumentIdRef || "")}
                    onRemove={(id) => setConfirmModal({ open: true, id })}
                    onPreview={onPreviewDocument}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        title="Remove associated document"
        message="Are you sure you want to remove this associated document?"
        confirmText={pendingAction === "remove" ? "Removing…" : "Remove"}
        confirmButtonClass="btn-danger"
        icon="fas fa-exclamation-triangle text-danger"
        onClose={() => {
          if (isBusy) return;
          setConfirmModal({ open: false, id: null });
        }}
        onConfirm={handleConfirmRemove}
      />
    </div>
  );
}

export default QmsVaultAssociatedDocsPanel;
